import '.a{display:flex;}.ssr-plugin'
import '.aj{gap:0.5rem;}.ssr-plugin'
import '.n{align-items:center;}.ssr-plugin'
import '.a5{position:fixed;}.ssr-plugin'
import '.e5{min-width:12rem;}.ssr-plugin'
import '.ae{background-color:var(--shades-white);}.ssr-plugin'
import '.db{border-radius:10px;}.ssr-plugin'
import '.e6{border:1px solid var(--Grays-Grays-100, #F6F6F6);}.ssr-plugin'
import '.e7{box-shadow:0 3px 10px 0 rgba(0, 0, 0, 0.05);}.ssr-plugin'
import '.e8{z-index:9999;}.ssr-plugin'
import '.l{overflow:hidden;}.ssr-plugin'
import '.e9{padding:0.75rem 1.25rem;}.ssr-plugin'
import '.ap{cursor:pointer;}.ssr-plugin'
import '.d8{justify-content:flex-start;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import { useLanguage } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { Tabs } from '@app/components/core/tabs/Tabs'
import { match } from 'ts-pattern'
import { useParamState } from '@app/util/hooks/useParamState'
import { Overview } from './tabContent/Overview'
import { Initiatives } from './tabContent/Initiatives'
import { Current } from './tabContent/Current'
import { Thread } from '@app/components/blocks/thread/Thread'
import { Locations } from './tabContent/Locations'
import { StepByStep } from './tabContent/StepByStep'
import { DevelopmentAreas } from './tabContent/DevelopmentAreas'
import { useState, useEffect, useRef } from 'react'
import { styled, Style } from 'inlines'
import { Text } from '@app/components/core/typography/Text'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons'
import { Subsidies } from './tabContent/Subsidies'
import useClickOutside from '@app/util/hooks/useClickOutside'
import { MunicipalityViewModel } from 'functions/app/geography/view/municipality'

const tabs = {
  overview: 'overview',
  discussion: 'discussion',
  initiatives: 'initiatives',
  current: 'current',
  locations: 'locations',
  developmentAreas: 'developmentAreas',
  stepByStep: 'stepByStep',
  subsidies: 'subsidies',
} as const
type Tab = keyof typeof tabs

export const MunicipalityTabContent = ({
  municipality,
}: {
  municipality: MunicipalityViewModel
}) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.regionPage
  const [tab, setTab] = useParamState(tabs, tabs.overview)

  // TODO: Enable more tabs once there's content
  // const initialVisibleTab: Tab[] = [
  //   'overview',
  //   'discussion',
  //   'initiatives',
  //   'current',
  // ]
  // const initialDropdownTab: Tab[] = [
  //   'locations',
  //   'developmentAreas',
  //   'stepByStep',
  //   'subsidies',
  // ]

  const initialVisibleTab: Tab[] = [
    'overview',
    'discussion',
    'initiatives',
    'locations',
  ]

  const [visibleTab, setVisibleTab] = useState<Tab[]>(initialVisibleTab)
  const [dropdownTab, setDropdownTab] = useState<Tab[]>([])

  const handleDropdownTabSelect = (selectedTab: Tab) => {
    // Find the tab to swap (last visible tab before "More")
    const tabToSwap = visibleTab[visibleTab.length - 1]
    setVisibleTab([...visibleTab.slice(0, -1), selectedTab])
    setDropdownTab([...dropdownTab.filter((t) => t !== selectedTab), tabToSwap])
    setTab(selectedTab)
  }

  const getTabLabel = (tabKey: Tab): string => {
    return translations[tabKey]
  }

  return (
    <Column alignItems="stretch" gap="2rem">
      <Tabs.Group selectedTab={tab}>
        {[
          ...visibleTab.map((tabKey) => (
            <Tabs.Tab
              key={tabKey}
              label={getTabLabel(tabKey)}
              tabKey={tabs[tabKey]}
              onSelectTab={setTab}
            />
          )),
          <MoreDropdownTab
            key="more"
            label={translations.more}
            dropdownTab={dropdownTab}
            selectedTab={tab}
            onSelectTab={handleDropdownTabSelect}
            getTabLabel={getTabLabel}
          />,
        ]}
      </Tabs.Group>
      {match(tab)
        .with(tabs.overview, () => (
          <Overview key="overview" entity={municipality} />
        ))
        .with(tabs.discussion, () => (
          <Thread key="thread" threadId={municipality.thread?.id} />
        ))
        .with(tabs.initiatives, () => (
          <Initiatives key="initiatives" entity={municipality} />
        ))
        .with(tabs.locations, () => (
          <Locations key="locations" entity={municipality} />
        ))
        .with(tabs.current, () => (
          <Current key="current" region={municipality} />
        ))
        .with(tabs.developmentAreas, () => (
          <DevelopmentAreas key="developmentAreas" region={municipality} />
        ))
        .with(tabs.stepByStep, () => (
          <StepByStep key="stepByStep" region={municipality} />
        ))
        .with(tabs.subsidies, () => (
          <Subsidies key="subsidies" region={municipality} />
        ))
        .exhaustive()}
    </Column>
  )
}

// More Dropdown Tab Component
type MoreDropdownTabProps = {
  label: string
  dropdownTab: Tab[]
  selectedTab: string
  onSelectTab: (tab: Tab) => void
  getTabLabel: (tab: Tab) => string
}

const MoreDropdownTab = ({
  label,
  dropdownTab,
  selectedTab,
  onSelectTab,
  getTabLabel,
}: MoreDropdownTabProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLButtonElement>(null)

  // Check if any dropdown tab is selected
  const isAnyDropdownTabSelected = dropdownTab.includes(selectedTab as Tab)

  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  // Update position when opening or scrolling
  useEffect(() => {
    if (!isOpen || typeof window === 'undefined') {
      return
    }

    const updatePosition = () => {
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
        })
      }
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen])

  useClickOutside(dropdownRef, () => setIsOpen(false))

  const handleTabSelect = (tabKey: Tab) => {
    onSelectTab(tabKey)
    setIsOpen(false)
  }

  const handleToggle = () => {
    setIsOpen((prev) => !prev)
  }

  if (dropdownTab.length === 0) {
    return null
  }

  const tabStyle: Style = {
    padding: '1.5rem',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
    cursor: 'pointer',
    position: 'relative',
    background: 'none',
    border: 'none',
    overflow: 'visible', // Important: allow dropdown to overflow
    '&:hover': {
      color: 'var(--primary-blue)',
    },
    borderBottom: `2px ${
      isAnyDropdownTabSelected ? 'var(--primary-blue)' : 'transparent'
    } solid`,
  }

  const textStyle: Style = {
    wordWrap: 'break-word',
    color: isAnyDropdownTabSelected
      ? 'var(--primary-blue)'
      : 'var(--shades-blue-300)',
    '&:hover': {
      color: 'var(--primary-blue)',
    },
  }

  return (
    <styled.button
      ref={dropdownRef}
      style={tabStyle}
      onClick={handleToggle}
      type="button"
    >
      <div className="a aj n"
      >
        <Text.Medium style={textStyle}>{label}</Text.Medium>
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{
            color: isAnyDropdownTabSelected
              ? 'var(--primary-blue)'
              : 'var(--shades-blue-300)',
            fontSize: '0.875rem',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </div>

      {isOpen && (
        <div className="a5 e5 ae db e6 e7 e8 l"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,}}
        >
          {dropdownTab.map((tabKey, index) => (
            <styled.div className="e9 ap a d8 n"
              key={tabKey}
              onClick={(e) => {
                e.stopPropagation()
                handleTabSelect(tabKey)
              }}
              style={{
                backgroundColor:
                  tabKey === selectedTab
                    ? 'var(--gray-100)'
                    : 'var(--shades-white)',
                borderBottom:
                  index < dropdownTab.length - 1 ? '1px solid #F6F6F6' : 'none',
                '&:hover': {
                  backgroundColor: 'var(--gray-100)',
                },
              }}
            >
              <Text.Medium
                style={{
                  color: 'var(--primary-blue)',
                  fontSize: '1rem',
                }}
              >
                {getTabLabel(tabKey)}
              </Text.Medium>
            </styled.div>
          ))}
        </div>
      )}
    </styled.button>
  )
}
